// Module Gestion des Tarifs - Super Admin
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { FiDollarSign, FiSave, FiRefreshCw, FiEdit2, FiCheck, FiX, FiCheckCircle } from 'react-icons/fi';
import { theme } from '@theme';
import { DashboardLayout } from '@/components/layout/DashboardLayout';

// Types
interface PricingConfig {
  id?: number;
  plan: 'starter' | 'professional' | 'enterprise';
  plan_display: string;
  billing_cycle: 'monthly' | 'quarterly' | 'yearly';
  billing_cycle_display: string;
  price: string;
  max_users: number;
  max_monthly_scorings: number;
  max_storage_gb: number;
  is_active: boolean;
}

interface OveragePricing {
  id?: number;
  overage_type: 'scoring' | 'storage_gb' | 'user';
  overage_type_display: string;
  price: string;
  is_active: boolean;
}

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xl};
`;

const Header = styled.div`
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.accent} 100%);
  padding: ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.lg};
  color: white;
  box-shadow: ${theme.shadows.lg};
`;

const HeaderTitle = styled.h1`
  font-size: ${theme.typography.sizes['3xl']};
  font-weight: ${theme.typography.weights.bold};
  margin: 0 0 ${theme.spacing.xs} 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
`;

const HeaderSubtitle = styled.p`
  font-size: ${theme.typography.sizes.base};
  margin: 0;
  opacity: 0.95;
`;

const TabsContainer = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
  padding: ${theme.spacing.sm};
  display: flex;
  gap: ${theme.spacing.sm};
`;

const Tab = styled.button<{ $active?: boolean }>`
  padding: ${theme.spacing.md} ${theme.spacing.xl};
  border: none;
  border-radius: ${theme.borderRadius.md};
  background: ${props => props.$active ? theme.colors.primary : 'transparent'};
  color: ${props => props.$active ? 'white' : theme.colors.text};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${props => props.$active ? theme.colors.primary : theme.colors.surface};
  }

  svg {
    font-size: 18px;
  }
`;

const ActionBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
  padding: ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
`;

const ActionBarTitle = styled.div`
  font-size: ${theme.typography.sizes.lg};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.md};
  border: none;
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  transition: all ${theme.transitions.fast};

  ${props => props.variant === 'primary' ? `
    background: ${theme.colors.success};
    color: white;
    &:hover {
      background: ${theme.colors.success}DD;
      transform: translateY(-2px);
      box-shadow: ${theme.shadows.md};
    }
  ` : `
    background: ${theme.colors.surface};
    color: ${theme.colors.text};
    &:hover {
      background: ${theme.colors.surface}DD;
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Section = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
  padding: ${theme.spacing.xl};
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.lg};
  padding-bottom: ${theme.spacing.md};
  border-bottom: 2px solid ${theme.colors.surface};
`;

const SectionTitle = styled.h2`
  font-size: ${theme.typography.sizes.xl};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background: ${theme.colors.surface}50;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid ${theme.colors.surface};

  &:hover {
    background: ${theme.colors.surface}30;
  }
`;

const TableHeaderCell = styled.th`
  padding: ${theme.spacing.md};
  text-align: left;
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text}80;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TableCell = styled.td`
  padding: ${theme.spacing.md};
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text};
`;

const Input = styled.input`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: 2px solid ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  width: 100%;
  max-width: 120px;
  transition: all ${theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }

  &:disabled {
    background: ${theme.colors.surface}50;
    cursor: not-allowed;
  }
`;

const Badge = styled.span<{ type?: string }>`
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.sizes.xs};
  font-weight: ${theme.typography.weights.semibold};

  ${props => {
    switch (props.type) {
      case 'monthly':
        return `background: ${theme.colors.info}20; color: ${theme.colors.info};`;
      case 'quarterly':
        return `background: ${theme.colors.warning}20; color: ${theme.colors.warning};`;
      case 'yearly':
        return `background: ${theme.colors.success}20; color: ${theme.colors.success};`;
      default:
        return `background: ${theme.colors.surface}; color: ${theme.colors.text};`;
    }
  }}
`;

const PlanBadge = styled.span<{ plan?: string }>`
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.sizes.xs};
  font-weight: ${theme.typography.weights.bold};
  text-transform: uppercase;
  letter-spacing: 0.5px;

  ${props => {
    switch (props.plan) {
      case 'starter':
        return `background: ${theme.colors.info}; color: white;`;
      case 'professional':
        return `background: ${theme.colors.primary}; color: white;`;
      case 'enterprise':
        return `background: ${theme.colors.accent}; color: white;`;
      default:
        return `background: ${theme.colors.surface}; color: ${theme.colors.text};`;
    }
  }}
`;

const IconButton = styled.button`
  background: transparent;
  border: none;
  color: ${theme.colors.primary};
  cursor: pointer;
  padding: ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.primary}10;
  }

  svg {
    font-size: 18px;
  }
`;

const InfoBox = styled.div`
  background: ${theme.colors.info}10;
  border-left: 4px solid ${theme.colors.info};
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  margin-bottom: ${theme.spacing.lg};
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}80;
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const LoadingSpinner = styled.div`
  width: 50px;
  height: 50px;
  border: 4px solid ${theme.colors.surface};
  border-top: 4px solid ${theme.colors.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

export const PricingManagement: React.FC = () => {
  const navigate = useNavigate();
  const [pricingConfigs, setPricingConfigs] = useState<PricingConfig[]>([]);
  const [overagePricings, setOveragePricings] = useState<OveragePricing[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editMode, setEditMode] = useState<{ [key: string]: boolean }>({});

  // Mock data - À remplacer par les vraies API calls
  useEffect(() => {
    loadPricingData();
  }, []);

  const loadPricingData = async () => {
    setIsLoading(true);
    try {
      // TODO: Remplacer par de vrais appels API
      // const response = await fetch('/api/v1/pricing/');
      // const data = await response.json();

      // Mock data pour démonstration
      setPricingConfigs([
        {
          id: 1,
          plan: 'starter',
          plan_display: 'Starter',
          billing_cycle: 'monthly',
          billing_cycle_display: 'Mensuel',
          price: '99.00',
          max_users: 5,
          max_monthly_scorings: 100,
          max_storage_gb: 10,
          is_active: true
        },
        {
          id: 2,
          plan: 'starter',
          plan_display: 'Starter',
          billing_cycle: 'quarterly',
          billing_cycle_display: 'Trimestriel',
          price: '270.00',
          max_users: 5,
          max_monthly_scorings: 100,
          max_storage_gb: 10,
          is_active: true
        },
        {
          id: 3,
          plan: 'starter',
          plan_display: 'Starter',
          billing_cycle: 'yearly',
          billing_cycle_display: 'Annuel',
          price: '990.00',
          max_users: 5,
          max_monthly_scorings: 100,
          max_storage_gb: 10,
          is_active: true
        },
        {
          id: 4,
          plan: 'professional',
          plan_display: 'Professional',
          billing_cycle: 'monthly',
          billing_cycle_display: 'Mensuel',
          price: '299.00',
          max_users: 20,
          max_monthly_scorings: 500,
          max_storage_gb: 50,
          is_active: true
        },
        {
          id: 5,
          plan: 'professional',
          plan_display: 'Professional',
          billing_cycle: 'quarterly',
          billing_cycle_display: 'Trimestriel',
          price: '810.00',
          max_users: 20,
          max_monthly_scorings: 500,
          max_storage_gb: 50,
          is_active: true
        },
        {
          id: 6,
          plan: 'professional',
          plan_display: 'Professional',
          billing_cycle: 'yearly',
          billing_cycle_display: 'Annuel',
          price: '2990.00',
          max_users: 20,
          max_monthly_scorings: 500,
          max_storage_gb: 50,
          is_active: true
        },
        {
          id: 7,
          plan: 'enterprise',
          plan_display: 'Enterprise',
          billing_cycle: 'monthly',
          billing_cycle_display: 'Mensuel',
          price: '999.00',
          max_users: 100,
          max_monthly_scorings: 999999,
          max_storage_gb: 500,
          is_active: true
        },
        {
          id: 8,
          plan: 'enterprise',
          plan_display: 'Enterprise',
          billing_cycle: 'quarterly',
          billing_cycle_display: 'Trimestriel',
          price: '2700.00',
          max_users: 100,
          max_monthly_scorings: 999999,
          max_storage_gb: 500,
          is_active: true
        },
        {
          id: 9,
          plan: 'enterprise',
          plan_display: 'Enterprise',
          billing_cycle: 'yearly',
          billing_cycle_display: 'Annuel',
          price: '9990.00',
          max_users: 100,
          max_monthly_scorings: 999999,
          max_storage_gb: 500,
          is_active: true
        }
      ]);

      setOveragePricings([
        {
          id: 1,
          overage_type: 'scoring',
          overage_type_display: 'Scoring supplémentaire',
          price: '0.50',
          is_active: true
        },
        {
          id: 2,
          overage_type: 'storage_gb',
          overage_type_display: 'Stockage supplémentaire (par GB)',
          price: '2.00',
          is_active: true
        },
        {
          id: 3,
          overage_type: 'user',
          overage_type_display: 'Utilisateur supplémentaire',
          price: '10.00',
          is_active: true
        }
      ]);
    } catch (error) {
      console.error('Erreur lors du chargement des tarifs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveAll = async () => {
    setIsSaving(true);
    try {
      // TODO: Implémenter la sauvegarde via API
      // await Promise.all([
      //   ...pricingConfigs.map(config =>
      //     fetch(`/api/v1/pricing/${config.id}/`, {
      //       method: 'PATCH',
      //       body: JSON.stringify(config)
      //     })
      //   ),
      //   ...overagePricings.map(pricing =>
      //     fetch(`/api/v1/overage-pricing/${pricing.id}/`, {
      //       method: 'PATCH',
      //       body: JSON.stringify(pricing)
      //     })
      //   )
      // ]);

      console.log('Sauvegarde des tarifs:', { pricingConfigs, overagePricings });
      alert('✅ Tarifs sauvegardés avec succès !');
      setEditMode({});
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      alert('❌ Erreur lors de la sauvegarde des tarifs');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRefresh = () => {
    loadPricingData();
  };

  const updatePricingConfig = (id: number, field: keyof PricingConfig, value: any) => {
    setPricingConfigs(configs =>
      configs.map(config =>
        config.id === id ? { ...config, [field]: value } : config
      )
    );
  };

  const updateOveragePricing = (id: number, field: keyof OveragePricing, value: any) => {
    setOveragePricings(pricings =>
      pricings.map(pricing =>
        pricing.id === id ? { ...pricing, [field]: value } : pricing
      )
    );
  };

  const toggleEditMode = (type: 'plan' | 'overage', id: number) => {
    const key = `${type}-${id}`;
    setEditMode(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <LoadingOverlay>
          <LoadingSpinner />
        </LoadingOverlay>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Container>
        <Header>
          <HeaderTitle>
            <FiDollarSign /> Gestion des Tarifs
          </HeaderTitle>
          <HeaderSubtitle>
            Configuration des prix des abonnements et des dépassements
          </HeaderSubtitle>
        </Header>

        <TabsContainer>
          <Tab $active={false} onClick={() => navigate('/superadmin/subscriptions')}>
            <FiCheckCircle /> Abonnements
          </Tab>
          <Tab $active={true}>
            <FiDollarSign /> Tarifs
          </Tab>
        </TabsContainer>

        <ActionBar>
          <ActionBarTitle>Tarifs actifs: {pricingConfigs.length + overagePricings.length}</ActionBarTitle>
          <ActionButtons>
            <Button variant="secondary" onClick={handleRefresh} disabled={isSaving}>
              <FiRefreshCw /> Actualiser
            </Button>
            <Button variant="primary" onClick={handleSaveAll} disabled={isSaving}>
              <FiSave /> {isSaving ? 'Sauvegarde...' : 'Sauvegarder tout'}
            </Button>
          </ActionButtons>
        </ActionBar>

        <Section>
          <SectionHeader>
            <SectionTitle>📋 Tarifs des Plans d'Abonnement</SectionTitle>
          </SectionHeader>

          <InfoBox>
            💡 Ces tarifs définissent le prix des abonnements selon le plan et le cycle de facturation. Modifiez-les pour ajuster vos prix.
          </InfoBox>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Plan</TableHeaderCell>
                <TableHeaderCell>Cycle</TableHeaderCell>
                <TableHeaderCell>Prix (FCFA)</TableHeaderCell>
                <TableHeaderCell>Utilisateurs</TableHeaderCell>
                <TableHeaderCell>Scorings/mois</TableHeaderCell>
                <TableHeaderCell>Stockage (GB)</TableHeaderCell>
                <TableHeaderCell>Actions</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <tbody>
              {pricingConfigs.map(config => {
                const isEditing = editMode[`plan-${config.id}`];
                return (
                  <TableRow key={config.id}>
                    <TableCell>
                      <PlanBadge plan={config.plan}>{config.plan_display}</PlanBadge>
                    </TableCell>
                    <TableCell>
                      <Badge type={config.billing_cycle}>{config.billing_cycle_display}</Badge>
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        step="0.01"
                        value={config.price}
                        onChange={(e) => updatePricingConfig(config.id!, 'price', e.target.value)}
                        disabled={!isEditing}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        value={config.max_users}
                        onChange={(e) => updatePricingConfig(config.id!, 'max_users', parseInt(e.target.value))}
                        disabled={!isEditing}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        value={config.max_monthly_scorings}
                        onChange={(e) => updatePricingConfig(config.id!, 'max_monthly_scorings', parseInt(e.target.value))}
                        disabled={!isEditing}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        value={config.max_storage_gb}
                        onChange={(e) => updatePricingConfig(config.id!, 'max_storage_gb', parseInt(e.target.value))}
                        disabled={!isEditing}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton onClick={() => toggleEditMode('plan', config.id!)}>
                        {isEditing ? <FiCheck /> : <FiEdit2 />}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </tbody>
          </Table>
        </Section>

        <Section>
          <SectionHeader>
            <SectionTitle>💰 Tarifs des Dépassements</SectionTitle>
          </SectionHeader>

          <InfoBox>
            💡 Ces tarifs s'appliquent lorsqu'un client dépasse les limites de son plan (scorings, stockage, utilisateurs).
          </InfoBox>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Type de dépassement</TableHeaderCell>
                <TableHeaderCell>Prix unitaire (FCFA)</TableHeaderCell>
                <TableHeaderCell>Statut</TableHeaderCell>
                <TableHeaderCell>Actions</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <tbody>
              {overagePricings.map(pricing => {
                const isEditing = editMode[`overage-${pricing.id}`];
                return (
                  <TableRow key={pricing.id}>
                    <TableCell>{pricing.overage_type_display}</TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        step="0.01"
                        value={pricing.price}
                        onChange={(e) => updateOveragePricing(pricing.id!, 'price', e.target.value)}
                        disabled={!isEditing}
                      />
                    </TableCell>
                    <TableCell>
                      <Badge type={pricing.is_active ? 'success' : 'error'}>
                        {pricing.is_active ? 'Actif' : 'Inactif'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <IconButton onClick={() => toggleEditMode('overage', pricing.id!)}>
                        {isEditing ? <FiCheck /> : <FiEdit2 />}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </tbody>
          </Table>
        </Section>
      </Container>

      {isSaving && (
        <LoadingOverlay>
          <LoadingSpinner />
        </LoadingOverlay>
      )}
    </DashboardLayout>
  );
};
